import React from 'react';
import { Form } from 'react-bootstrap';

interface ConnectionDatabaseProps {
  connectionData: any;
  handleParameterChange: (key: string, value: any) => void;
}

const ConnectionDatabase: React.FC<ConnectionDatabaseProps> = ({ connectionData, handleParameterChange }) => {
  const databaseType = connectionData.connection_parameters.database_type || '';

  const isMssql = databaseType === 'Microsoft SQL Server';

  const mssqlAuthMethod = connectionData.connection_parameters.mssql_auth_method || 'username_password';

  const isCertAuth = isMssql && mssqlAuthMethod === 'service_principal_certificate';

  const handlePfxFileChange = (file: File | null) => {
    if (!file) {
      handleParameterChange('pfx_file', null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (!result) return;

      const bytes = new Uint8Array(result as ArrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      handleParameterChange('pfx_file', base64);
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <Form.Group controlId="databaseType">
        <Form.Label>Database Type</Form.Label>
        <Form.Control as="select" value={databaseType} onChange={(e) => handleParameterChange('database_type', e.target.value)}>
          <option value="">Select Database Type</option>
          <option value="PostgreSQL">PostgreSQL</option>
          <option value="Microsoft SQL Server">Microsoft SQL Server</option>
          <option value="Oracle">Oracle</option>
        </Form.Control>
      </Form.Group>

      <Form.Group controlId="host">
        <Form.Label>Host</Form.Label>
        <Form.Control type="text" value={connectionData.connection_parameters.host || ''} onChange={(e) => handleParameterChange('host', e.target.value)} />
      </Form.Group>

      <Form.Group controlId="port">
        <Form.Label>Port</Form.Label>
        <Form.Control
          type="text"
          value={connectionData.connection_parameters.port || ''}
          onChange={(e) => handleParameterChange('port', parseInt(e.target.value, 10) || 0)}
        />
      </Form.Group>

      <Form.Group controlId="database">
        <Form.Label>Database</Form.Label>
        <Form.Control
          type="text"
          value={connectionData.connection_parameters.database || ''}
          onChange={(e) => handleParameterChange('database', e.target.value)}
        />
      </Form.Group>

      {isMssql && (
        <Form.Group controlId="mssqlAuthMethod">
          <Form.Label>MSSQL Authentication</Form.Label>
          <Form.Control as="select" value={mssqlAuthMethod} onChange={(e) => handleParameterChange('mssql_auth_method', e.target.value)}>
            <option value="username_password">Username &amp; Password</option>
            <option value="service_principal_certificate">Service Principal (Certificate)</option>
          </Form.Control>
        </Form.Group>
      )}

      {/* Default: Username/password */}
      {(!isMssql || !isCertAuth) && (
        <>
          <Form.Group controlId="username">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              value={connectionData.connection_parameters.username || ''}
              onChange={(e) => handleParameterChange('username', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="password">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              value={connectionData.connection_parameters.password || ''}
              onChange={(e) => handleParameterChange('password', e.target.value)}
            />
          </Form.Group>
        </>
      )}

      {/* MSSQL: Service Principal + Certificate */}
      {isCertAuth && (
        <>
          <Form.Group controlId="tenantId">
            <Form.Label>Tenant ID</Form.Label>
            <Form.Control
              type="text"
              value={connectionData.connection_parameters.tenant_id || ''}
              onChange={(e) => handleParameterChange('tenant_id', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="clientId">
            <Form.Label>Client ID</Form.Label>
            <Form.Control
              type="text"
              value={connectionData.connection_parameters.client_id || ''}
              onChange={(e) => handleParameterChange('client_id', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="pfxFile">
            <Form.Label>PFX File (.pfx)</Form.Label>
            <Form.Control type="file" accept=".pfx" onChange={(e: any) => handlePfxFileChange(e.target.files?.[0] || null)} />
            {connectionData.connection_parameters.pfx_file && <Form.Text className="text-muted">PFX loaded.</Form.Text>}
          </Form.Group>

          <Form.Group controlId="pfxPassword">
            <Form.Label>PFX Password</Form.Label>
            <Form.Control
              type="password"
              value={connectionData.connection_parameters.pfx_password || ''}
              onChange={(e) => handleParameterChange('pfx_password', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="pemPassphrase">
            <Form.Label>PEM Passphrase (optional)</Form.Label>
            <Form.Control
              type="password"
              value={connectionData.connection_parameters.pem_passphrase || ''}
              onChange={(e) => handleParameterChange('pem_passphrase', e.target.value)}
            />
          </Form.Group>
        </>
      )}

      {/* Conditional rendering for Oracle-specific property */}
      {databaseType === 'Oracle' && (
        <Form.Group controlId="privilege">
          <Form.Label>Privilege</Form.Label>
          <Form.Control
            type="text"
            value={connectionData.connection_parameters.privilege || ''}
            onChange={(e) => handleParameterChange('privilege', e.target.value)}
          />
        </Form.Group>
      )}
    </>
  );
};

export default ConnectionDatabase;
